import { GoogleAuthProvider, linkWithPopup, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { AUTH_LOGIN, DATABASE, FUNCTIONS } from '../Config/firebase';
import {
  clearPanelBrowserKeys,
  finishPanelSignIn,
  googleProviderParameters,
  linkedAccount,
  rememberPanelBrowserKeys,
  resolvePanelAccess,
  shouldSendLoginAlert,
} from './panelAdminAccess';

export function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters(googleProviderParameters());
  return provider;
}

export async function signInPanelWithGoogle() {
  const cred = await signInWithPopup(AUTH_LOGIN, createGoogleProvider());
  return finishPanelSignIn({
    user: cred && cred.user,
    evaluate: evaluatePanelUser,
    accept: acceptPanelSignIn,
    reject: rejectPanelAccess,
  });
}

export async function linkGoogleToCurrentAdmin(currentUser) {
  const user = currentUser || AUTH_LOGIN.currentUser;
  if (!user || !user.uid) {
    const err = new Error('Sign in with the admin password first.');
    err.code = 'auth/no-current-user';
    throw err;
  }
  const beforeUid = user.uid;
  const cred = await linkWithPopup(user, createGoogleProvider());
  const linked = linkedAccount(beforeUid, cred && cred.user);
  if (!linked.ok) {
    await rejectPanelAccess();
    const err = new Error('Google link would change the account.');
    err.code = 'auth/uid-changed';
    throw err;
  }
  return cred.user;
}

function browserStorage() {
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
}

async function readPanelUser(uid) {
  return getDoc(doc(DATABASE, 'users', uid));
}

export async function evaluatePanelUser(user) {
  return resolvePanelAccess({
    user,
    getIdTokenResult: (force) => user.getIdTokenResult(force),
    readUser: readPanelUser,
  });
}

export async function rejectPanelAccess() {
  clearPanelBrowserKeys(browserStorage());
  try {
    await signOut(AUTH_LOGIN);
  } catch (_err) {
    // A sessão já estava encerrada.
  }
}

async function sendLoginAlert() {
  try {
    const notify = httpsCallable(FUNCTIONS, 'notifyAdminLogin');
    await notify({});
  } catch (err) {
    const code = err && (err.code || err.message);
    console.warn('SEC-011: aviso de login não enviado', code || 'erro');
  }
}

export async function acceptPanelSignIn(user) {
  rememberPanelBrowserKeys(browserStorage(), user);
  if (shouldSendLoginAlert('submit')) {
    await sendLoginAlert();
  }
}

export function watchPanelSession(onState) {
  return onAuthStateChanged(AUTH_LOGIN, async (user) => {
    if (!user) {
      clearPanelBrowserKeys(browserStorage());
      onState({ status: 'signed-out' });
      return;
    }

    const decision = await evaluatePanelUser(user);
    if (decision.status !== 'ok') {
      await rejectPanelAccess();
      onState({ status: 'denied' });
      return;
    }

    rememberPanelBrowserKeys(browserStorage(), user);
    onState({ status: 'ok', user });
  });
}
